import { useCallback } from "react";
import { AppProvider } from "@shopify/polaris";
import translations from "@shopify/polaris/locales/pt-BR.json";
import "@shopify/polaris/build/esm/styles.css";
import {
  isShopifyEmbedded as isShopifyWeb,
  isShopifyPOS as isShopifyPos,
} from "@shopify/app-bridge-utils";
import { useNavigate as useAppBridgeNavigate } from "@shopify/app-bridge-react";
import { useNavigate as useBrowserRouterNavigate } from "react-router-dom";

let useNavigate = null;
if (isShopifyWeb() || isShopifyPos()) {
  useNavigate = useAppBridgeNavigate;
} else {
  useNavigate = useBrowserRouterNavigate;
}

/**
 * Sets up the AppProvider from Polaris.
 * @desc PolarisProvider passes a custom link component to Polaris.
 * The Link component handles navigation within an embedded app.
 * Prefer using this vs any other method such as an anchor.
 * Use it by importing Link from Polaris, e.g:
 *
 * ```
 * import {Link} from '@shopify/polaris'
 *
 * function MyComponent() {
 *  return (
 *    <div><Link url="/tab2">Tab 2</Link></div>
 *  )
 * }
 * ```
 *
 * PolarisProvider also passes translations to Polaris.
 *
 */
export default function PolarisProvider({ children }) {
  return (
    <AppProvider i18n={translations} linkComponent={AppBridgeLink}>
      {children}
    </AppProvider>
  );
}

function AppBridgeLink({ url, children, external, ...rest }) {
  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    navigate(url);
  }, [url]);

  const IS_EXTERNAL_LINK_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/;

  if (external || IS_EXTERNAL_LINK_REGEX.test(url)) {
    return (
      <a target="_blank" rel="noopener noreferrer" href={url} {...rest}>
        {children}
      </a>
    );
  }

  return (
    // eslint-disable-next-line
    <a onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
